import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD } from '@/components/ui/table';
import { apiMessage, formatQuantity } from '../shared';
import { formatSignedQuantity, subtractDecimal, sumDecimal, signOfDecimal } from './signedDecimal';
import type { WarehouseOption } from './PurchaseOrderForm';

export interface ReceivableLine {
  id: string;
  inventoryItemId: string;
  quantity: string;
  receivedQuantity: string;
  unitCost: string;
  lineTotal: string;
  inventoryItem: { id: string; name: string; sku: string | null } | null;
  unit: { id: string; code: string } | null;
}

export interface ReceivableOrder {
  id: string;
  number: string;
  supplier: { id: string; name: string } | null;
  warehouse?: { id: string; name: string } | null;
  lines?: ReceivableLine[];
}

/** §21 — an unsigned decimal literal; a delivery is never negative. */
const DECIMAL = /^\d+(\.\d+)?$/;

const nameOf = (line: ReceivableLine) => line.inventoryItem?.name ?? line.inventoryItemId;

/**
 * A goods receipt as a checklist against the order it fulfils.
 *
 * ## Four columns the frame draws that are not built
 *
 * `Condition` (Good / Damaged), `Expiry`, and the per-line `Notes` and
 * per-discrepancy `Reason` have nowhere to go. `PurchaseReceiptLine` is
 * `{inventoryItemId, unitId, purchaseOrderLineId, quantity, unitCost,
 * lineTotal}` and the line DTO declares five properties; `StockMovement` has no
 * batch, lot or expiry column either, so expiry is a new model plus a FEFO
 * consumption policy rather than a field. Under `forbidNonWhitelisted` one
 * undeclared property on one line 400s the whole delivery, so:
 *
 * - `Condition` and `Expiry` are **dropped**.
 * - `Notes` and `Reason` are **kept as controls and folded into the header
 *   `notes`**, prefixed with the item they belong to, because that field is real
 *   and the information is worth keeping. Nothing per line reaches the wire.
 * - `Attach discrepancy photos` is dropped: there is no attachment model, and no
 *   multipart route outside catalog.
 *
 * ## One button, not two
 *
 * The frame draws `Save partial` and `Complete receiving`. Both would call
 * `POST /procurement/receipts` with the same body: partial versus complete is
 * not a parameter, it is read off the line quantities server-side — *"the status
 * is not a decision, it is a reading of the lines"*. Two buttons doing the same
 * thing under different names is a promise the server does not keep.
 *
 * ## And the guard that stays
 *
 * `Outstanding` is shown per line and is the ceiling a delivery may not exceed.
 * The server refuses an over-receipt; answering it here, per line, is what stops
 * a second delivery double-booking the first one's quantity.
 */
export function ReceiveStockSheet({
  order,
  loading,
  error,
  onRetry,
  warehouses,
  onCancel,
  onReceived,
}: {
  order: ReceivableOrder | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  warehouses: WarehouseOption[];
  onCancel: () => void;
  onReceived: () => void;
}) {
  const { t } = useTranslation();

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [warehouseId, setWarehouseId] = useState(order?.warehouse?.id ?? '');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Keyed by purchase-order line id, never by index: the lines arrive with the
   * order, after this sheet mounts, so an index-keyed draft would bind a
   * quantity to whichever row happened to land in that position.
   */
  const rows = useMemo(
    () =>
      (order?.lines ?? []).map((line) => {
        const outstanding = subtractDecimal(line.quantity, line.receivedQuantity);
        const outstandingText =
          signOfDecimal(outstanding) < 0 ? '0' : formatSignedQuantity(outstanding);
        const typed = (quantities[line.id] ?? outstandingText).trim();
        const malformed = typed !== '' && !DECIMAL.test(typed);
        const over = !malformed && typed !== '' && signOfDecimal(subtractDecimal(typed, outstandingText)) > 0;
        const shortage =
          !malformed && typed !== '' && !over ? subtractDecimal(outstandingText, typed) : null;

        return {
          line,
          typed,
          outstanding: outstandingText,
          delivered: !malformed && typed !== '' && signOfDecimal(typed) > 0,
          shortage: shortage && signOfDecimal(shortage) > 0 ? shortage : null,
          error: malformed
            ? t('platform.procurement.quantityInvalid')
            : over
              ? t('platform.procurement.exceedsOutstanding', {
                  qty: outstandingText,
                  unit: line.unit?.code ?? '',
                })
              : null,
        };
      }),
    [order, quantities, t],
  );

  const discrepancies = rows.filter((row) => row.delivered && row.shortage);

  const summary = useMemo(() => {
    const ordered = sumDecimal(rows.map((row) => row.outstanding));
    const received = sumDecimal(rows.filter((row) => row.delivered).map((row) => row.typed));
    return { ordered, received, discrepancies: discrepancies.length };
  }, [discrepancies.length, rows]);

  const blocked = useMemo(() => {
    const bad = rows.find((row) => row.error);
    if (bad) return bad.error;
    // A line left at zero is simply not being delivered; a receipt with no
    // positive line has nothing to book.
    return rows.some((row) => row.delivered) ? null : t('platform.procurement.receiptNeedsLines');
  }, [rows, t]);

  /**
   * Per-line remarks, gathered into the one field that exists for them. Each is
   * prefixed with its item so the sentence survives being flattened.
   */
  const composedNotes = useMemo(() => {
    const perLine = rows
      .map((row) => {
        const reason = (reasons[row.line.id] ?? '').trim();
        return reason ? `${nameOf(row.line)}: ${reason}` : null;
      })
      .filter((entry): entry is string => entry !== null);
    return [notes.trim(), ...perLine].filter(Boolean).join('\n');
  }, [notes, reasons, rows]);

  const submit = async () => {
    if (!order || blocked) return;
    setBusy(true);
    try {
      await api.post('/procurement/receipts', {
        purchaseOrderId: order.id,
        ...(warehouseId ? { warehouseId } : {}),
        ...(invoiceNumber.trim() ? { invoiceNumber: invoiceNumber.trim() } : {}),
        ...(composedNotes ? { notes: composedNotes } : {}),
        lines: rows
          .filter((row) => row.delivered)
          .map((row) => ({
            inventoryItemId: row.line.inventoryItemId,
            purchaseOrderLineId: row.line.id,
            quantity: row.typed,
          })),
      });
      toast.success(t('platform.procurement.received'));
      onReceived();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">
          {t('platform.procurement.receiveStock')}
        </h2>
        <p className="text-sm text-ink-muted">
          {order
            ? t('platform.procurement.receiveAgainst', 'Against {{number}} from {{supplier}}', {
                number: order.number,
                supplier: order.supplier?.name ?? '—',
              })
            : t('platform.procurement.receiveHint')}
        </p>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={onRetry} />
      ) : loading && !order ? (
        <LoadingState label={t('common.loading')} />
      ) : !rows.length ? (
        <EmptyState title={t('platform.procurement.receiptNeedsLines')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          {/* `min-w-0` on both tracks: the checklist scrolls inside its own
              `TableWrap`, it does not widen the page. */}
          <Card className="min-w-0">
            <CardHeader>
              <CardTitle>{t('platform.procurement.receiveTitle', { number: order?.number ?? '' })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {/* The document the whole delivery exists to be matched against. */}
                <Field label={t('platform.procurement.invoice')}>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </Field>
                {/* Required on `PurchaseReceipt`; omitting it only works while
                    the order already names one. */}
                <Field label={t('platform.procurement.warehouse')}>
                  <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                    <option value="">—</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('platform.procurement.item')}</TH>
                      <TH className="text-end">{t('platform.procurement.ordered')}</TH>
                      <TH className="text-end">{t('platform.procurement.outstandingShort')}</TH>
                      <TH>{t('platform.procurement.receivedQty')}</TH>
                      <TH>{t('operations.inventory.unit')}</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {rows.map((row) => (
                      <TR key={row.line.id}>
                        <TD className="font-medium">
                          {nameOf(row.line)}
                          {row.error && (
                            <p className="text-xs font-normal text-red-600 dark:text-red-400">
                              {row.error}
                            </p>
                          )}
                        </TD>
                        <TD className="text-end tabular-nums text-ink-muted">
                          {formatQuantity(row.line.quantity)}
                        </TD>
                        <TD className="text-end tabular-nums">{row.outstanding}</TD>
                        <TD className="w-28">
                          <Input
                            className="h-9"
                            inputMode="decimal"
                            aria-label={nameOf(row.line)}
                            aria-invalid={row.error ? true : undefined}
                            value={row.typed}
                            onChange={(e) =>
                              setQuantities((prev) => ({ ...prev, [row.line.id]: e.target.value }))
                            }
                          />
                        </TD>
                        <TD className="text-ink-muted">{row.line.unit?.code ?? '—'}</TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>

              <p className="rounded-lg border bg-subtle p-3 text-sm">
                {t(
                  'platform.procurement.receiveSummary',
                  'Receiving {{received}} of {{ordered}} outstanding · {{discrepancies}} short',
                  {
                    received: formatSignedQuantity(summary.received),
                    ordered: formatSignedQuantity(summary.ordered),
                    discrepancies: summary.discrepancies,
                  },
                )}
              </p>
              {/* The two footer buttons the frame draws would send the same body
                  to the same route; the server reads partial vs complete off the
                  quantities. Said once, here, rather than implied by a button. */}
              <p className="text-xs text-ink-muted">
                {t('platform.procurement.partialIsDerived')}
              </p>
            </CardContent>
          </Card>

          <div className="min-w-0 space-y-4">
            <Card className="h-fit">
              <CardHeader>
                <CardTitle>
                  {t('platform.procurement.discrepancyReport')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!discrepancies.length ? (
                  <p className="text-sm text-ink-muted">
                    {t('platform.procurement.noDiscrepancies')}
                  </p>
                ) : (
                  discrepancies.map((row) => (
                    <div key={row.line.id} className="space-y-2 rounded-lg border p-3">
                      <p className="text-sm font-medium">{nameOf(row.line)}</p>
                      <dl className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <dt className="text-ink-muted">{t('platform.procurement.ordered')}</dt>
                          <dd className="tabular-nums">{row.outstanding}</dd>
                        </div>
                        <div>
                          <dt className="text-ink-muted">
                            {t('platform.procurement.receivedQty')}
                          </dt>
                          <dd className="tabular-nums">{formatSignedQuantity(row.typed)}</dd>
                        </div>
                        <div>
                          <dt className="text-ink-muted">
                            {t('platform.procurement.shortage')}
                          </dt>
                          <dd
                            className="tabular-nums text-red-600 dark:text-red-400"
                            data-testid={`shortage-${row.line.id}`}
                          >
                            {formatSignedQuantity(row.shortage ?? '0')}
                          </dd>
                        </div>
                      </dl>
                      {/* Free text, not a select: there is no reason enum to
                          populate one from, and inventing a fixed list would be
                          asserting a taxonomy the product does not have. */}
                      <Input
                        className="h-9"
                        aria-label={t('platform.procurement.reasonFor', 'Reason — {{item}}', {
                          item: nameOf(row.line),
                        })}
                        placeholder={t('platform.procurement.reason')}
                        value={reasons[row.line.id] ?? ''}
                        onChange={(e) =>
                          setReasons((prev) => ({ ...prev, [row.line.id]: e.target.value }))
                        }
                      />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <Field
                  label={t('platform.procurement.notes')}
                  hint={t('platform.procurement.receiptNotesHint')}
                >
                  <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Field>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={busy || !order || !!blocked}
          title={blocked ?? undefined}
        >
          {t('platform.procurement.receive')}
        </Button>
      </div>
    </div>
  );
}
